import React, { useState, useEffect, useMemo } from "react";
import ReportViewer from "./ReportViewerComponent";
import { Checkbox } from "@progress/kendo-react-inputs";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import Icon from "components/Global/icon";
import styles from "./Reports.module.scss";
import { SectionDividerFull } from "components/Contact/sectionDivider";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import { baseURL, ReportURL } from "api/axios.config";
import { useCompany } from "services/companyService";
import { useActiveCurrencies } from "services/currencyService";

const MatterBalances = () => {
  const { data: company } = useCompany();
  const { data: activeCurrenciesRaw } = useActiveCurrencies();

  const multiCurrencyEnabled = Boolean(
    company?.modules?.MultiCurrency ?? company?.Modules?.MultiCurrency
  );
  const baseCurrency = company?.baseCurrency;

  const activeCurrencies = useMemo(
    () =>
      (activeCurrenciesRaw || []).map((c) => ({
        ...c,
        displayName: `${c.currencyCode} - ${c.name}`,
      })),
    [activeCurrenciesRaw]
  );

  const DateFormat = (value) => {
    let date = new Date(value);
    return date.toISOString().split("T")[0];
  };

  const orderData = [
    {
      name: "Matter Ref",
      value: "MatterRef",
    },
    {
      name: "Client Name",
      value: "ClientName",
    },
    {
      name: "Details",
      value: "Details",
    },
    {
      name: "Application",
      value: "AppCode",
    },
    {
      name: "Fee Earner",
      value: "FeeEarnerRef",
    },
    {
      name: "Start Date",
      value: "StartDate",
    },
  ];

  const groupData = [
    {
      name: "None",
      value: "NULL",
    },
    {
      name: "Client",
      value: "ClientName",
    },
    {
      name: "Application",
      value: "AppCode",
    },
    {
      name: "Fee Earner",
      value: "FeeEarnerRef",
    },
  ];

  let date = new Date();

  const analyzeParameters = () => {
    const isBaseCurrency =
      !selectedCurrency || selectedCurrency.currencyCode === baseCurrency;
    const newReportName = isBaseCurrency
      ? "MatterBalances.trdp"
      : "MatterBalancesCurrency.trdp";
    setReportName(newReportName);
    setParameters({
      Type: type,
      ExcludeZero: excludeZero,
      ExcludeClosed: excludeClosed,
      ToDate: includeEndDate ? DateFormat(endDate) : undefined,
      OrderBy: orderbyType?.value,
      GroupBy: groupByType?.value,
      ...(!isBaseCurrency && { CurrencyCode: selectedCurrency.currencyCode }),
    });
    setShouldLoad(true);
  };

  const [type, setType] = useState("");
  const [orderbyType, setOrderByType] = useState({
    value: "matterRef",
    name: "Matter Ref",
  });
  const [groupByType, setGroupByType] = useState({
    name: "None",
    value: "NULL",
  });
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [reportName, setReportName] = useState("MatterBalances.trdp");

  const [includeEndDate, setIncludeEndDate] = useState(false);
  const [excludeZero, setExcludeZero] = useState(true);
  const [excludeClosed, setExcludeClosed] = useState(true);
  const [show, setShow] = useState(window.innerWidth);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [endDate, setEndDate] = React.useState(date);

  const [parameters, setParameters] = useState({
    Type: type,
    ExcludeZero: excludeZero,
    ExcludeClosed: excludeClosed ? 1 : null,
    ToDate: includeEndDate ? DateFormat(endDate) : undefined,
    OrderBy: orderbyType?.value,
  });

  useEffect(() => {
    if (baseCurrency && activeCurrencies?.length) {
      const found = activeCurrencies.find(
        (c) => c.currencyCode === baseCurrency
      );
      if (found) setSelectedCurrency(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseCurrency, activeCurrencies?.length]);

  const onChangeEndSelect = (event) => {
    setEndDate(event.value);
    setIncludeEndDate(true);
  };

  const UpdateButton = (
    <Button className="common-btn" onClick={analyzeParameters}>
      Update
    </Button>
  );

  const toDatePicker = (
    <div className="d-flex align-items-center justify-content-center">
      <Checkbox
        value={includeEndDate}
        className="ms-2 me-1 mt-auto mb-auto"
        onChange={(e) => {
          setIncludeEndDate(e.value);
        }}
        label={""}
      />
      <DatePicker
        id="to"
        className="date-picker"
        value={endDate || null}
        onChange={onChangeEndSelect}
        toggleButton={CalenderCustomToggleButton}
      />
    </div>
  );

  const IncludeCheckbox = (
    <Checkbox
      // defaultChecked={false}
      value={excludeZero}
      className="checkbox-label"
      onChange={(e) => {
        setExcludeZero(e.value);
      }}
      label={"Exclude Zero Balances"}
    />
  );

  const IncludeClosed = (
    <Checkbox
      // defaultChecked={false}
      value={excludeClosed}
      className="checkbox-label"
      onChange={(e) => {
        setExcludeClosed(e.value);
      }}
      label={"Exclude Closed"}
    />
  );

  const orderByDropDown = (
    <div className="">
      <ComboBox
        className=""
        iconClassName="wa"
        icon={<Icon icon="arrow-down" />}
        data={orderData}
        required
        value={orderbyType}
        onChange={(e) => {
          setOrderByType(e.value);
        }}
        dataItemKey={"value"}
        textField={"name"}
        placeholder="Order by"
        style={{ maxWidth: "325px", height: "40px" }}
      />
    </div>
  );

  const groupByDropDown = (
    <div className="">
      <ComboBox
        className="ps-1"
        iconClassName="wa"
        icon={<Icon icon="arrow-down" />}
        data={groupData}
        required
        value={groupByType}
        onChange={(e) => {
          setGroupByType(e.value);
        }}
        dataItemKey={"value"}
        textField={"name"}
        placeholder="Group by"
        style={{ maxWidth: "325px", height: "40px" }}
      />
    </div>
  );

  const currencyDropDown = multiCurrencyEnabled ? (
    <div className="">
      <ComboBox
        iconClassName="wa"
        icon={<Icon icon="arrow-down" />}
        data={activeCurrencies}
        value={selectedCurrency}
        onChange={(e) => setSelectedCurrency(e.value)}
        dataItemKey="currencyCode"
        textField="displayName"
        placeholder="Currency"
        style={{ maxWidth: "325px", height: "40px" }}
      />
    </div>
  ) : null;

  const reportViewer = (
    <ReportViewer
      name={reportName}
      parameters={parameters}
      shouldLoad={shouldLoad}
      setShouldLoad={setShouldLoad}
      url={`${ReportURL}/reporting/reports`}
    />
  );

  useEffect(() => {
    function handleResize() {
      if (show > 1400 && window.innerWidth < 1400) {
        setShow(window.innerWidth);
      } else if (show < 1400 && window.innerWidth > 1400) {
        setShow(window.innerWidth);
      }
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      {show < 1400 ? (
        <div
          className={`container-fluid w-100 ps-4 pt-2 ${styles[`report-small-screen`]
            }`}
        >
          <div className="mt-4 d-flex flex-wrap">
            <div className=" me-3">
              <div className="nominal-form-to-parent-row" style={{
                width: '150px'
              }}>
                <div className="labels-containers">
                  <div className="label-to">
                    <Label editorId="to">To</Label>
                  </div>
                </div>
              </div>
              <div className="nominal-form-to-row">
                <div className="date-form-to-wrapper">
                  <div className="to">{toDatePicker}</div>
                </div>
              </div>
            </div>
            <div className="d-flex flex-column me-lg-3">
              <Label className="label mb-1">Order By</Label>
              {orderByDropDown}
            </div>
            <div className="d-flex flex-column me-lg-3">
              <Label className="label mb-1">Group By</Label>
              {groupByDropDown}
            </div>
            {multiCurrencyEnabled && (
              <div className="d-flex flex-column me-lg-3">
                <Label className="label mb-1">Currency</Label>
                {currencyDropDown}
              </div>
            )}
            <div className="mt-4 pt-2 me-5">{IncludeCheckbox}</div>
            <div className="mt-4 pt-2 me-5">{IncludeClosed}</div>
            <div className="mt-4 pt-2">{UpdateButton}</div>
          </div>
          <div className={`row mt-4 ${styles[`reportBackground`]} 1fr`}>
            {reportViewer}
          </div>
        </div>
      ) : (
        <div
          className={`container-fluid w-100 p-4 pt-2 mt-4 ${styles[`report-large-screen`]
            }`}
        >
          <div className="row h-100">
            <div className="col" style={{ maxWidth: "300px" }}>
              <div
                className={`p-4 pt-4 ${styles[`reportBackground`]} ${styles[`height`]
                  }`}
              >
                <p className={`${styles[`heading`]} fs-lg`}>Filters</p>
                <div className="d-flex flex-column mt-4">
                  <Label className="label mb-1">To</Label>
                  <div className="no-borders">{toDatePicker}</div>
                </div>
                <SectionDividerFull sectionName={""} />
                <div className="d-flex flex-column pt-3">
                  <Label className="label mb-1">Order By</Label>
                  {orderByDropDown}
                </div>
                <div className="d-flex flex-column pt-2">
                  <Label className="label mb-1">Group By</Label>
                  {groupByDropDown}
                </div>
                {multiCurrencyEnabled && (
                  <div className="d-flex flex-column pt-2">
                    <Label className="label mb-1">Currency</Label>
                    {currencyDropDown}
                  </div>
                )}
                <SectionDividerFull sectionName={""} />
                <div className="pt-3">{IncludeClosed}</div>
                <div className="pt-3">{IncludeCheckbox}</div>
                <SectionDividerFull sectionName={""} />
                <div className="pt-3 float-end">{UpdateButton}</div>
              </div>
            </div>
            <div className="col">
              <div className={`row ${styles[`reportBackground`]} 1fr`}>
                {reportViewer}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MatterBalances;
